import React from "react";
import type {
  ArticleListTeaserBlock,
  ArticleTeaserItem,
} from "@/types/content";
import { ChevronRightIcon } from "@/components/icons";
import { useLocalizedTexts, t } from "@/utils/translations";
import { responsiveCmsImage } from "@/utils/cmsImage";
import styles from "./ArticleListSection.module.css";

/**
 * A card's width in a full-width block (the 1168px grid, 2rem gaps): 568px in
 * two columns, 368px in three. Three columns drop to two below 1024px, and
 * every grid to one below 768px.
 */
const CARD_IMAGE_SIZES = {
  "2": "(max-width: 767px) 100vw, (max-width: 1200px) 50vw, 568px",
  "3": "(max-width: 767px) 100vw, (max-width: 1023px) 50vw, (max-width: 1200px) 33vw, 368px",
} as const;

export interface ArticleListSectionProps {
  block: ArticleListTeaserBlock;
  className?: string;
}

interface ArticleCardProps {
  article: ArticleTeaserItem;
  localizedTexts: ReturnType<typeof useLocalizedTexts>;
  imageSizes: string;
}

/**
 * Article Card - individual article card with image and title from teaser data
 */
const ArticleCard: React.FC<ArticleCardProps> = ({
  article,
  localizedTexts,
  imageSizes,
}) => {
  const navigation = article.compositions?.navigation;
  const hero = article.compositions?.contentHero;
  const title = navigation?.link?.name ?? "";
  const articleUrl = navigation?.link?.url;
  const image = navigation?.image ?? hero?.hero?.media ?? null;

  const cardContent = (
    <>
      <div className={styles.imageWrapper}>
        {image ? (
          <img
            {...responsiveCmsImage(image.url, imageSizes, image.width)}
            alt={image.altText || title}
            className={styles.image}
            loading="lazy"
            width={image.width || undefined}
            height={image.height || undefined}
          />
        ) : (
          <div className={styles.imagePlaceholder} />
        )}
      </div>

      <div className={styles.content}>
        <h3 className={styles.title}>{title}</h3>

        <span className={styles.readLink} aria-hidden="true">
          {t(localizedTexts, "Tracker.Articles.Read", "Read")}
          <ChevronRightIcon
            className={styles.readLinkIcon}
            aria-hidden="true"
          />
        </span>
      </div>
    </>
  );

  if (articleUrl) {
    return (
      <article className={styles.card}>
        <a
          href={articleUrl}
          className={styles.cardLink}
          aria-label={`${t(localizedTexts, "Tracker.Articles.ReadArticle", "Read article")}: ${title}`}
        >
          {cardContent}
        </a>
      </article>
    );
  }

  return <article className={styles.card}>{cardContent}</article>;
};

/**
 * Article List Section - displays a grid of article teaser cards with optional heading
 * Data comes from the backend ArticleListTeaserBlockViewModel
 */
export const ArticleListSection: React.FC<ArticleListSectionProps> = ({
  block,
  className,
}) => {
  const localizedTexts = useLocalizedTexts();
  const { heading, linkText, link, teaserItems } = block;

  if (!teaserItems || teaserItems.length === 0) {
    return null;
  }

  const columns = teaserItems.length <= 2 ? "2" : "3";

  const containerClasses = [styles.section, className]
    .filter(Boolean)
    .join(" ");
  const gridClasses = [styles.grid, styles[`cols${columns}`]]
    .filter(Boolean)
    .join(" ");

  return (
    <section className={containerClasses} data-component="article-list-section">
      {heading && (
        <div className={styles.header}>
          <h2 className={styles.heading}>{heading}</h2>
        </div>
      )}

      <div
        className={gridClasses}
        role="list"
        aria-label={t(localizedTexts, "Tracker.Articles.Articles", "Articles")}
      >
        {teaserItems.map((article, index) => (
          <div
            key={article.id || index}
            className={styles.gridItem}
            role="listitem"
          >
            <ArticleCard
              article={article}
              localizedTexts={localizedTexts}
              imageSizes={CARD_IMAGE_SIZES[columns]}
            />
          </div>
        ))}
      </div>

      {link?.url && (
        <div className={styles.footer}>
          <a
            href={link.url}
            className={styles.viewAllButton}
            target={link.target || "_self"}
            rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
          >
            {linkText ||
              t(
                localizedTexts,
                "Tracker.Articles.ReadMoreArticles",
                "Read more articles"
              )}
          </a>
        </div>
      )}
    </section>
  );
};

export default ArticleListSection;
